/**
 * Scoring, badges and stars.
 *
 * DESIGN.md §5: the end-of-level card is icon-heavy because the audience is 6-9
 * and half of them cannot read a results screen. Everything here therefore
 * resolves to an ICON plus a number, never to prose.
 *
 * The three badges are deliberately incompatible with each other. Ghost wants
 * you never seen; Hurricane wants you to break the entire house, which is very
 * hard to do unseen. Curious Cat wants you wandering the level ignoring both.
 * A player cannot grind all three by playing carefully - they have to choose a
 * way to play, which is what makes a replay a different game rather than a
 * faster one. Purrfect exists for the child who eventually does all three.
 *
 * Pure functions over plain data: no DOM, no Three.js. src/ui/levelCard.ts owns
 * how this looks.
 */

import { tuning } from '@/core/tuning';

export type BadgeId = 'ghost' | 'hurricane' | 'curiousCat' | 'purrfect';

/** Everything the card and the save file need, sampled when the level is won. */
export interface LevelResult {
  levelId: string;
  /** Chaos earned during play, before badge bonuses. */
  chaos: number;
  cozy: number;
  keysCollected: number;
  /** Needed to finish. Two of three, by design - see DESIGN.md §5. */
  keysRequired: number;
  /** Every key the level defines, so the card can show 2/3 rather than 3/2. */
  keysTotal: number;
  /** Times granny got a full Contact lock. Zero is a Ghost run. */
  detections: number;
  breakablesBroken: number;
  breakablesTotal: number;
  momentsFound: number;
  momentsTotal: number;
  elapsedSeconds: number;
}

export interface Badge {
  id: BadgeId;
  icon: string;
  label: string;
  /** One short line explaining how it is earned, for the child who missed it. */
  hint: string;
  earned: boolean;
  /** Chaos added when earned. */
  bonus: number;
}

export interface ScoredResult {
  result: LevelResult;
  badges: Badge[];
  earned: BadgeId[];
  /** Chaos from play. */
  chaos: number;
  /** Chaos from badges. */
  bonus: number;
  total: number;
  stars: 0 | 1 | 2 | 3;
}

/**
 * Which badges a run earned.
 *
 * Hurricane and Curious Cat require the level to actually contain something to
 * break or find: awarding "you broke everything" for a level with no breakables
 * would hand out a free badge and quietly devalue it everywhere else.
 */
export function badgesFor(result: LevelResult): Badge[] {
  const score = tuning.score;

  const ghost = result.detections === 0;
  const hurricane = result.breakablesTotal > 0 && result.breakablesBroken >= result.breakablesTotal;
  const curiousCat = result.momentsTotal > 0 && result.momentsFound >= result.momentsTotal;

  return [
    {
      id: 'ghost',
      icon: '👻',
      label: 'Ghost',
      hint: 'Never be seen',
      earned: ghost,
      bonus: score.ghostBonus,
    },
    {
      id: 'hurricane',
      icon: '🌪️',
      label: 'Hurricane',
      hint: 'Break everything',
      earned: hurricane,
      bonus: score.hurricaneBonus,
    },
    {
      id: 'curiousCat',
      icon: '😻',
      label: 'Curious Cat',
      hint: 'Find every cat moment',
      earned: curiousCat,
      bonus: score.curiousCatBonus,
    },
    {
      id: 'purrfect',
      icon: '🏆',
      label: 'Purrfect',
      hint: 'Earn the other three',
      earned: ghost && hurricane && curiousCat,
      // No bonus of its own: it is recognition for the other three, and paying
      // twice for the same run would make the star thresholds meaningless.
      bonus: 0,
    },
  ];
}

/** Stars from the final total, using the thresholds in tuning. */
export function starsFor(total: number): 0 | 1 | 2 | 3 {
  const stars = tuning.score.stars;
  if (total >= stars.three) return 3;
  if (total >= stars.two) return 2;
  if (total >= stars.one) return 1;
  return 0;
}

/** The whole card, computed once when the level is won. */
export function scoreLevel(result: LevelResult): ScoredResult {
  const badges = badgesFor(result);
  const earned = badges.filter((badge) => badge.earned);
  const bonus = earned.reduce((sum, badge) => sum + badge.bonus, 0);
  const total = result.chaos + bonus;

  return {
    result,
    badges,
    earned: earned.map((badge) => badge.id),
    chaos: result.chaos,
    bonus,
    total,
    stars: starsFor(total),
  };
}

/** `m:ss`, because a 7-year-old reads a clock and not 214 seconds. */
export function formatDuration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}
